import React, { memo } from 'react'
import { Box, Chip } from '@mui/material'
import { Cancel } from '@mui/icons-material'
import { TFunction } from 'i18next'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import { LocationItem, LocationsPanelMode } from './types'

export type LocationChipProps = {
  item: LocationItem
  isEdit: boolean
  showRadius: boolean
  selected: boolean
  highlighted: boolean
  onToggle: (index: number) => void
  onRemove: (index: number) => void
  t: TFunction
}

export const LocationChip = memo(function LocationChip({
  item,
  isEdit,
  showRadius,
  selected,
  highlighted,
  onToggle,
  onRemove,
  t,
}: LocationChipProps) {
  const radius = showRadius && item.radius ? t('adgroup.geoTargeting.panel.radiusValue', { radius: item.radius }) : ''

  return (
    <Chip
      size="small"
      variant={selected ? 'filled' : 'outlined'}
      color={selected ? 'primary' : highlighted ? 'warning' : 'default'}
      // The area has no column here: it shows on hover
      title={item.area?.label}
      label={
        <>
          {item.name}
          {radius && (
            <Box component="span" sx={{ ml: 0.75, opacity: 0.75, fontVariantNumeric: 'tabular-nums' }}>
              {radius}
            </Box>
          )}
        </>
      }
      onClick={isEdit ? () => onToggle(item.index) : undefined}
      onDelete={isEdit ? () => onRemove(item.index) : undefined}
      aria-pressed={isEdit ? selected : undefined}
      aria-label={
        isEdit
          ? t('adgroup.geoTargeting.panel.selectLocation', { name: radius ? `${item.name}, ${radius}` : item.name })
          : undefined
      }
      deleteIcon={<Cancel aria-label={t('adgroup.geoTargeting.panel.removeLocation', { name: item.name })} />}
    />
  )
})

type LocationsChipsProps = {
  items: LocationItem[]
  mode: LocationsPanelMode
  showRadius: boolean
  selected: ReadonlySet<number>
  highlightedIndex?: number
  onToggle: (index: number) => void
  onRemove: (index: number) => void
  label: string
}

/** The denser view: the current page as wrapping chips; in edit mode a click selects, the cross removes */
const LocationsChips: React.FC<LocationsChipsProps> = ({
  items,
  mode,
  showRadius,
  selected,
  highlightedIndex,
  onToggle,
  onRemove,
  label,
}) => {
  const t = useAppTranslations()
  const isEdit = mode === 'edit'

  return (
    <Box
      component="ul"
      aria-label={label}
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, m: 0, px: 1.5, py: 1.25, listStyle: 'none' }}
    >
      {items.map((item) => (
        <li key={item.index}>
          <LocationChip
            item={item}
            isEdit={isEdit}
            showRadius={showRadius}
            selected={isEdit && selected.has(item.index)}
            highlighted={item.index === highlightedIndex}
            onToggle={onToggle}
            onRemove={onRemove}
            t={t}
          />
        </li>
      ))}
    </Box>
  )
}

export default LocationsChips
